import React, { useContext, useEffect, useMemo, useState } from 'react';

import { DateTime } from 'luxon';
import TimeDuration from '../TimeDuration/index';
import { Stack } from '@mui/system';
import { Box, styled, Chip } from '@mui/material';
import { PostData } from '../model/types';
import { Link } from 'react-router-dom';
import HealthBar from '../HealthBar/HealthBar';
import PostActions from './PostActions';
import '../fonts.css';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import AvatarIcon from '../resource/AvatarIcon';
import { replaceTagsWithLinks } from '../lib';

import TombstoneCustomIcon from '../resource/TombstoneCustom';
import LinkPreviewCard, { extractPreviewUrl } from '../components/LinkPreviewCard';
import QuotedPostCard from '../components/QuotedPostCard';
import PollDisplay from '../components/PollDisplay';
import EmojiReactions from '../components/EmojiReactions';
import ShareTombstone from '../components/ShareTombstone';
import CapsuleDonate from '../components/CapsuleDonate';
import WipePlusBadge from '../components/WipePlusBadge';
import { isBuried, formatOpenAt, formatTimeUntilOpen } from '../lib/timeCapsule';
import { formatSparkRemaining, sparkProgress } from '../lib/sparks';
import SparkChainReply from '../components/SparkChainReply';
import LockIcon from '@mui/icons-material/Lock';
import BoltIcon from '@mui/icons-material/Bolt';
import { AuthContext, MyContext } from '../context/AuthContext';


const UsernameLink = styled(Link)`
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  color: inherit;
`;

// Regex to detect GIF URLs in post content
const GIF_URL_REGEX =
  /(https?:\/\/\S+\.gif|https?:\/\/media\.giphy\.com\/\S+|https?:\/\/tenor\.com\/\S+)/gi;

// Detect internal post quote URLs: any URL containing /posts/<UUID>
const INTERNAL_POST_REGEX =
  /https?:\/\/[^\s]*\/posts\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;

// Extract YouTube video ID from various URL formats
function getYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

const YOUTUBE_URL_REGEX =
  /https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)[A-Za-z0-9_?=&%-]+/gi;


function hashColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash) + id.charCodeAt(i);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 42%)`;
}

function Post({
  post,
  expandToReply,
  depth = 0,
}: {
  post: PostData;
  expandToReply?: string;
  /**
   * Nesting depth for reply-tree rendering. 0 means a root-level post
   * (the normal feed / profile / bookmark call sites). Each level of
   * reply indirection increments by 1. `Replies` caps rendering at
   * REPLY_DEPTH_CAP and swaps the nested tree for a "Continue thread"
   * link when exceeded. Top-level call sites can safely omit this.
   */
  depth?: number;
}) {
  const getUserRecord =
    useContext<CacheContextType>(CacheContext).getUserRecord;
  const getPost = useContext<CacheContextType>(CacheContext).getPost;
  const getAllVotes = useContext<CacheContextType>(CacheContext).allVotes;
  const currentUserRecord = useContext<MyContext>(AuthContext).userRecord;
  const [postRecord, setPostRecord] = useState<PostData | undefined>(
    getPost(post.id),
  );
  const [postUserRecord, setPostUserRecord] = useState<any | undefined>();
  const [isExpired, setIsExpired] = useState(false);
  const [expiryDate, setExpiryDate] = useState<Date>(
    DateTime.fromISO(post.expire_at).toJSDate(),
  );
  const [createdTime, setCreatedTime] = useState<DateTime>(
    DateTime.fromISO(post.created_at),
  );
  const [isPermanent, setIsPermanent] = useState(false);
  const [processedContent, setProcessedContent] =
    useState<React.ReactNode>(null);
  const [expanded, setExpanded] = useState(false);
  // Sparks re-render every second so the MM:SS countdown ticks.
  const isSpark = postRecord?.is_spark === true;
  const [sparkTick, setSparkTick] = useState(0);
  useEffect(() => {
    if (!isSpark || isExpired) return;
    const id = setInterval(() => setSparkTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isSpark, isExpired]);

  const processPostContent = async (content: string) => {
    if (!content) return null;

    // Combined regex: internal post quotes first, then YouTube, then GIFs
    const COMBINED_REGEX = new RegExp(
      `${INTERNAL_POST_REGEX.source}|${YOUTUBE_URL_REGEX.source}|${GIF_URL_REGEX.source}`,
      'gi',
    );

    const matches: Array<{ url: string; start: number; end: number }> = [];
    let m: RegExpExecArray | null;
    COMBINED_REGEX.lastIndex = 0;
    while ((m = COMBINED_REGEX.exec(content)) !== null) {
      matches.push({ url: m[0], start: m.index, end: m.index + m[0].length });
    }

    if (matches.length === 0) {
      return replaceTagsWithLinks(content);
    }

    const result: React.ReactNode[] = [];
    let cursor = 0;
    matches.forEach(({ url, start, end }, idx) => {
      if (start > cursor) {
        result.push(
          <span key={`text-${idx}`}>{replaceTagsWithLinks(content.slice(cursor, start))}</span>,
        );
      }

      // Internal post quote
      const internalPostMatch = url.match(/\/posts\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
      if (internalPostMatch) {
        result.push(<QuotedPostCard key={`quote-${idx}`} postId={internalPostMatch[1]} />);
        cursor = end;
        return;
      }

      const ytId = getYouTubeId(url);
      if (ytId) {
        result.push(
          <Box key={`yt-${idx}`} sx={{ my: 2, position: 'relative', paddingTop: '56.25%', width: '100%' }}>
            <iframe
              src={`https://www.youtube.com/embed/${ytId}`}
              title="YouTube video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0,
                borderRadius: '8px',
              }}
            />
          </Box>,
        );
      } else {
        result.push(
          <Box key={`gif-${idx}`} sx={{ my: 2, maxWidth: '100%' }}>
            <img
              src={url}
              alt="GIF"
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                borderRadius: '8px',
                display: 'block',
                margin: '10px auto',
              }}
            />
          </Box>,
        );
      }

      cursor = end;
    });

    if (cursor < content.length) {
      result.push(
        <span key="text-tail">{replaceTagsWithLinks(content.slice(cursor))}</span>,
      );
    }

    return result;
  };

  useEffect(() => {
    setPostRecord(getPost(post.id));
    if (!postRecord) return;
    setExpiryDate(
      DateTime.fromISO(postRecord?.expire_at ?? '').toJSDate(),
    );
    setCreatedTime(DateTime.fromISO(postRecord?.created_at ?? ''));

    setIsPermanent(postRecord?.is_permanent);
    setIsExpired(expiryDate < new Date() && !postRecord?.is_permanent);
    if (!postRecord?.user_record_id || postRecord.user_record_id === undefined)
      return;

    setPostUserRecord(getUserRecord(postRecord.user_record_id));

    // Process the post content to display GIFs
    if (postRecord?.value) {
      processPostContent(postRecord.value).then((processed) => {
        setProcessedContent(processed);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postRecord, getPost]);

  // True when a downvote actually pushed expire_at past now — the vote
  // row sets killed=true on the finishing blow. Replaces the old
  // "created < 24h ago?" heuristic which was neither precise nor
  // reliable (a 1-day post expiring naturally would false-positive).
  const wasKilledByVote = useMemo((): boolean => {
    if (!isExpired || !postRecord) return false;
    const allVotes = getAllVotes();
    return allVotes.some(
      (v) => v.parent_id === postRecord.id && v.object_type === 'posts' && v.killed,
    );
  }, [isExpired, postRecord?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate how much upvote time is needed to revive the post
  const getTimeNeededToRevive = (): number => {
    if (!isExpired || !postRecord) return 0;

    // Calculate seconds needed to reach the current time
    const currentTime = DateTime.now().toSeconds();
    const expirySeconds = DateTime.fromJSDate(expiryDate).toSeconds();

    // Add a buffer of 1 hour to ensure the post stays alive for a meaningful time
    const secondsNeeded = currentTime - expirySeconds + 3600;

    // Convert to hours and round up to make it clear
    return Math.ceil(secondsNeeded / 3600);
  };

  // Compute accent color and time-bar percentage from health
  const { accentColor, timePct } = useMemo(() => {
    if (!postRecord) return { accentColor: '#9e9e9e', timePct: 0 };
    if (postRecord.is_permanent) return { accentColor: 'gold', timePct: 100 };
    if (isExpired) return { accentColor: '#616161', timePct: 0 };
    // Sparks: orange theme with a second-resolution countdown.
    if (postRecord.is_spark) {
      const progress = sparkProgress(postRecord.created_at, postRecord.expire_at);
      const pct = Math.max(0, Math.min(100, (1 - progress) * 100));
      return { accentColor: '#ff9800', timePct: pct };
    }
    const created = DateTime.fromISO(postRecord.created_at).toSeconds();
    const expire = DateTime.fromISO(postRecord.expire_at).toSeconds();
    const total = expire - created;
    if (total <= 0) return { accentColor: '#b51e1b', timePct: 0 };
    const left = expire - DateTime.now().toSeconds();
    const pct = Math.max(0, Math.min(100, (left / total) * 100));
    const t = pct / 100;
    const r = Math.round(181 * (1 - t) + 22 * t);
    const g = Math.round(30 * (1 - t) + 168 * t);
    return { accentColor: `rgb(${r},${g},27)`, timePct: pct };
  }, [postRecord?.expire_at, postRecord?.is_permanent, postRecord?.is_spark, isExpired, sparkTick]); // eslint-disable-line react-hooks/exhaustive-deps

  // Buried time capsule: render a locked placeholder. The author sees
  // their pending post with donation count; everyone else sees a sealed
  // card with a DONATE TIME button to help crack it open early.
  if (postRecord && isBuried(postRecord.open_at)) {
    const isOwnCapsule = currentUserRecord?.id === postRecord.user_record_id;
    const author = getUserRecord(postRecord.user_record_id);
    return (
      <Box
        sx={{
          boxSizing: 'border-box',
          margin: '0 auto',
          borderRadius: 0,
          border: '2px dashed',
          borderColor: 'divider',
          padding: 2,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          background: 'transparent',
        }}
        id={postRecord.id}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 48, height: 48,
              borderRadius: 0,
              bgcolor: 'transparent',
              border: '2px solid currentColor',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
              animation: 'capsulePulse 2.5s ease-in-out infinite',
              '@keyframes capsulePulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.55 },
              },
            }}
          >
            <LockIcon sx={{ color: 'text.primary', width: 24, height: 24 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontFamily: '"VT323", monospace',
                letterSpacing: 2,
                fontSize: '0.95rem',
                color: 'text.primary',
                fontWeight: 700,
                mb: 0.25,
              }}
            >
              {isOwnCapsule ? 'YOUR TIME CAPSULE · BURIED' : 'TIME CAPSULE · SEALED'}
            </Box>
            {!isOwnCapsule && author && (
              <Box sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 0.25 }}>
                buried by <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>@{author.username}</Box>
              </Box>
            )}
            <Box sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
              Opens <Box component="strong" sx={{ color: 'text.primary' }}>{formatOpenAt(postRecord.open_at!)}</Box>
              {' · '}in {formatTimeUntilOpen(postRecord.open_at!)}
            </Box>
            <Box sx={{ fontSize: '0.72rem', color: 'text.disabled', mt: 0.25, fontStyle: 'italic' }}>
              {isOwnCapsule
                ? 'Others can donate time to open it early. You\'ll see it surface in feeds when it opens.'
                : 'The content is sealed. Donate time from your wallet to shift the open-date earlier — if enough people chip in, it cracks open.'}
            </Box>
            {!isOwnCapsule && (
              <Box sx={{ mt: 1 }}>
                <CapsuleDonate post={postRecord} />
              </Box>
            )}
            {isOwnCapsule && (postRecord.capsule_donated_seconds ?? 0) > 0 && (
              <Box sx={{ mt: 0.5, fontSize: '0.72rem', color: '#b79dff', fontWeight: 700 }}>
                🎁 {Math.floor((postRecord.capsule_donated_seconds ?? 0) / 60)} minutes donated by others
              </Box>
            )}
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      data-post-card={postRecord?.id ?? 'unknown'}
      sx={{
        boxSizing: 'border-box',
        bgcolor: 'background.paper',
        margin: '0 auto',
        borderRadius: '6px',
        border: isSpark ? '1px dashed' : '1px solid',
        borderColor: isSpark ? 'rgba(255,152,0,0.45)' : 'divider',
        borderLeft: `3px solid ${accentColor}`,
        padding: 1.5,
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
          boxShadow: isSpark
            ? '0 2px 12px rgba(255,152,0,0.2)'
            : '0 2px 12px rgba(0,0,0,0.08)',
        },
        ...(isSpark && !isExpired && {
          background: (t) => t.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(255,152,0,0.04) 0%, rgba(255,152,0,0.01) 100%)'
            : 'linear-gradient(135deg, rgba(255,152,0,0.035) 0%, rgba(255,152,0,0.01) 100%)',
        }),
        // Critical pulse when < 1 hour remains (non-spark) OR sparks always pulse
        ...(!isExpired && !postRecord?.is_permanent && !isSpark && (() => {
          const left = DateTime.fromISO(postRecord?.expire_at ?? '').toSeconds() - DateTime.now().toSeconds();
          return left > 0 && left < 3600;
        })() && {
          animation: 'borderPulse 1.5s ease-in-out infinite',
          '@keyframes borderPulse': {
            '0%, 100%': { borderLeftColor: '#b51e1b' },
            '50%': { borderLeftColor: 'rgba(181,30,27,0.2)' },
          },
        }),
        ...(isSpark && !isExpired && {
          animation: 'sparkPulse 2s ease-in-out infinite',
          '@keyframes sparkPulse': {
            '0%, 100%': { borderLeftColor: '#ff9800' },
            '50%': { borderLeftColor: 'rgba(255,152,0,0.35)' },
          },
        }),
      }}
      id={postRecord?.id}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box style={{ width: 52, height: 52, flexShrink: 0 }}>
          <HealthBar objectRecord={postRecord ?? post}>
            {postUserRecord?.avatar_url ? (
              <img
                src={postUserRecord.avatar_url}
                alt={postUserRecord.username}
                style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: hashColor(postUserRecord?.id ?? ''), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AvatarIcon style={{ width: 28, height: 28, opacity: 0.9 }} />
              </Box>
            )}
          </HealthBar>
        </Box>
        <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
            <Box sx={{ fontSize: '15px', fontWeight: 700, color: 'text.primary', lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}>
              {!postUserRecord ? 'loading' : (
                <>
                  <UsernameLink to={'/' + postUserRecord.username}>
                    {postUserRecord.username}
                  </UsernameLink>
                  <WipePlusBadge user={postUserRecord} />
                </>
              )}
            </Box>
            <Box sx={{ color: 'text.secondary', fontSize: '13px' }}>
              @{postUserRecord?.username ?? ''}
            </Box>
            <Box sx={{ color: 'text.disabled', fontSize: '13px' }}>·</Box>
            <Box sx={{ color: 'text.secondary', fontSize: '13px' }}>
              <Link to={`/posts/${postRecord?.id ?? post.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                <TimeDuration jsDate={createdTime.toJSDate()} invertSign={true} titleDisplay="absoluteTime" /> ago
              </Link>
            </Box>
            <Box sx={{ color: 'text.disabled', fontSize: '13px' }}>·</Box>
            <Box sx={{ fontSize: '13px', color: isExpired ? 'error.main' : isPermanent ? 'warning.main' : isSpark ? '#ff9800' : 'text.secondary', fontVariantNumeric: 'tabular-nums', fontFamily: isSpark ? '"VT323", monospace' : 'inherit' }}>
              {isExpired ? (
                wasKilledByVote ? '☠ killed by downvote' : <>expired <TimeDuration jsDate={expiryDate} /> ago</>
              ) : isPermanent ? (
                'IMMORTAL'
              ) : isSpark ? (
                <>⚡ {formatSparkRemaining(postRecord?.expire_at ?? '')} left</>
              ) : (
                <><TimeDuration jsDate={expiryDate} /> left</>
              )}
            </Box>
            {isSpark && !isExpired && (
              <Chip
                icon={<BoltIcon sx={{ fontSize: 12 }} />}
                label="SPARK"
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.62rem',
                  fontFamily: '"VT323", monospace',
                  letterSpacing: 1,
                  bgcolor: 'rgba(255,152,0,0.14)',
                  color: '#ff9800',
                  border: '1px solid rgba(255,152,0,0.35)',
                  '& .MuiChip-label': { px: 0.5 },
                  '& .MuiChip-icon': { color: '#ff9800', ml: '2px', mr: '-4px' },
                }}
              />
            )}
            {/* Hot badge: shows when post has significant vote activity */}
            {!isExpired && (postRecord?.vote_count ?? 0) >= 3 && (() => {
              const ageHours = DateTime.now().diff(createdTime).as('hours');
              const score = (postRecord?.vote_count ?? 0) / Math.pow(ageHours + 2, 1.5);
              return score > 0.3;
            })() && (
              <Chip
                label="🔥 hot"
                size="small"
                sx={{ height: 16, fontSize: '0.65rem', bgcolor: 'rgba(255,80,0,0.12)', color: 'error.main', border: '1px solid rgba(255,80,0,0.25)', '& .MuiChip-label': { px: 0.75 } }}
              />
            )}
          </Box>
          <Box
            sx={{
              color: isExpired ? 'text.disabled' : 'text.primary',
              fontSize: '15px',
              fontWeight: 400,
              fontStyle: isExpired ? 'italic' : 'normal',
              lineHeight: 1.6,
              wordBreak: 'break-word',
            }}
          >
            {isExpired ? (
              <Stack direction="column" spacing={0.5} sx={{ width: '100%' }}>
                <Box sx={{ width: '240px', margin: 'auto' }}>
                  <TombstoneCustomIcon
                    className="font-face-nokia"
                    username={postUserRecord?.username ?? '???'}
                    from={DateTime.fromISO(
                      postRecord?.created_at ?? '',
                    ).toFormat("EEEE',' MMMM d',' ha")}
                    to={DateTime.fromISO(
                      postRecord?.expire_at ?? '',
                    ).toFormat("EEEE',' MMMM d',' ha")}
                    objectType="post"
                    style={{ width: '200px', height: '250px' }}
                  />
                  <Box>
                    {wasKilledByVote ? (
                      <>
                        This post was killed by downvotes.{' '}
                        {getTimeNeededToRevive() > 0
                          ? `Needs ${getTimeNeededToRevive()} hour${getTimeNeededToRevive() !== 1 ? 's' : ''} to revive.`
                          : 'Vote to resurrect it.'}
                      </>
                    ) : getTimeNeededToRevive() > 0 ? (
                      <>
                        Expired naturally. Needs {getTimeNeededToRevive()}{' '}
                        hour{getTimeNeededToRevive() !== 1 ? 's' : ''} to revive.
                      </>
                    ) : (
                      <>This post expired. Vote to revive it.</>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                    <ShareTombstone post={postRecord ?? post} />
                  </Box>
                </Box>
              </Stack>
            ) : (
              <Box>
                {(() => {
                  const rawText = (postRecord?.value ?? '').replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1').replace(/#\[([^\]]+)\]\([^)]+\)/g, '#$1');
                  const isLong = rawText.length > 400;
                  return (
                    <>
                      <Box
                        sx={isLong && !expanded ? {
                          maxHeight: '5.6em', // ~4 lines at 1.6 line-height
                          overflow: 'hidden',
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '2em',
                            background: (theme) => `linear-gradient(transparent, ${theme.palette.background.paper})`,
                          },
                        } : {}}
                      >
                        {processedContent || replaceTagsWithLinks(postRecord?.value ?? '')}
                      </Box>
                      {isLong && (
                        <Box
                          component="span"
                          onClick={() => setExpanded((e) => !e)}
                          sx={{ fontSize: '0.82rem', color: 'primary.main', cursor: 'pointer', display: 'inline-block', mt: 0.5, fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                        >
                          {expanded ? 'Show less' : 'Read more'}
                        </Box>
                      )}
                    </>
                  );
                })()}
                {(() => {
                  const previewUrl = extractPreviewUrl(postRecord?.value ?? '');
                  if (!previewUrl || /\/posts\/[0-9a-f-]{36}/i.test(previewUrl)) return null;
                  return <LinkPreviewCard url={previewUrl} />;
                })()}
                <PollDisplay postId={postRecord?.id ?? post.id} />
                <EmojiReactions objectId={postRecord?.id ?? post.id} objectType="posts" />
                {isSpark && <SparkChainReply parentSparkId={postRecord?.id ?? post.id} />}
              </Box>
            )}
          </Box>
          <PostActions
            post={postRecord ?? post}
            expandToReply={expandToReply}
            depth={depth}
          />
        </Stack>
      </Stack>

      {/* Time-remaining bar — depletes as the post ages toward expiry */}
      {!isExpired && !postRecord?.is_permanent && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            height: '2px',
            width: `${timePct}%`,
            bgcolor: accentColor,
            transition: 'width 60s linear, background-color 30s ease',
            borderRadius: '0 1px 0 0',
          }}
        />
      )}
    </Box>
  );
}

export default Post;
